import { useEffect, useState } from "react";

/**
 * Hash routes (#/devices, #/d/<eui>) so the same build works from any web address and inside
 * the phone app without server rewrites. The invite link is /invite#<token>, handled first.
 */
export type Route =
  | { name: "farm" }
  | { name: "devices" }
  | { name: "alerts"; tab?: string }
  | { name: "admin"; tab?: string }
  | { name: "programs" }
  | { name: "ask" }
  | { name: "reports" }
  | { name: "map" }
  | { name: "program"; id: string }
  | { name: "device"; eui: string }
  | { name: "device-setup"; eui: string }
  | { name: "accounts" }
  | { name: "account"; id: string }
  | { name: "new-account" }
  | { name: "server" }
  | { name: "shadow" }
  | { name: "console"; tab?: string }
  | { name: "toolbox" }
  | { name: "support"; id?: string; screen?: string }
  | { name: "paypal" }
  | { name: "me" };

export function parseRoute(hash: string): Route {
  const parts = hash.replace(/^#\/?/, "").split("/");
  if (parts[0] === "d" && parts[1] && parts[2] === "setup") return { name: "device-setup", eui: decodeURIComponent(parts[1]).toLowerCase() };
  if (parts[0] === "d" && parts[1]) return { name: "device", eui: decodeURIComponent(parts[1]).toLowerCase() };
  if (parts[0] === "accounts" && parts[1] === "new") return { name: "new-account" };
  if (parts[0] === "accounts" && parts[1]) return { name: "account", id: decodeURIComponent(parts[1]) };
  if (parts[0] === "accounts") return { name: "accounts" };
  if (parts[0] === "me") return { name: "me" };
  // #/support, #/support/new?screen, #/support/<ticket id>
  if (parts[0] === "support") {
    if (parts[1] === "new") return { name: "support", id: "new", ...(parts[2] ? { screen: decodeURIComponent(parts[2]) } : {}) };
    return { name: "support", ...(parts[1] ? { id: decodeURIComponent(parts[1]) } : {}) };
  }
  if (parts[0] === "server") return { name: "server" };
  if (parts[0] === "shadow") return { name: "shadow" };
  if (parts[0] === "toolbox") return { name: "toolbox" };
  if (parts[0] === "paypal") return { name: "paypal" };
  if (parts[0] === "console") return { name: "console", ...(parts[1] ? { tab: parts[1] } : {}) };
  if (parts[0] === "devices") return { name: "devices" };
  if (parts[0] === "alerts") return { name: "alerts", ...(parts[1] ? { tab: parts[1] } : {}) };
  if (parts[0] === "admin") return { name: "admin", ...(parts[1] ? { tab: parts[1] } : {}) };
  if (parts[0] === "programs" && parts[1]) return { name: "program", id: decodeURIComponent(parts[1]) };
  if (parts[0] === "programs") return { name: "programs" };
  if (parts[0] === "ask") return { name: "ask" };
  if (parts[0] === "reports") return { name: "reports" };
  if (parts[0] === "map") return { name: "map" };
  return { name: "farm" };
}

export function href(r: Route): string {
  switch (r.name) {
    case "device":
      return `#/d/${r.eui}`;
    case "device-setup":
      return `#/d/${r.eui}/setup`;
    case "accounts":
      return "#/accounts";
    case "account":
      return `#/accounts/${encodeURIComponent(r.id)}`;
    case "new-account":
      return "#/accounts/new";
    case "me":
      return "#/me";
    case "support":
      return r.id === "new" ? `#/support/new${r.screen ? `/${encodeURIComponent(r.screen)}` : ""}` : r.id ? `#/support/${encodeURIComponent(r.id)}` : "#/support";
    case "server":
      return "#/server";
    case "shadow":
      return "#/shadow";
    case "toolbox":
      return "#/toolbox";
    case "paypal":
      return "#/paypal";
    case "console":
      return r.tab ? `#/console/${r.tab}` : "#/console";
    case "devices":
      return "#/devices";
    case "alerts":
      return r.tab ? `#/alerts/${r.tab}` : "#/alerts";
    case "admin":
      return r.tab ? `#/admin/${r.tab}` : "#/admin";
    case "programs":
      return "#/programs";
    case "ask":
      return "#/ask";
    case "reports":
      return "#/reports";
    case "map":
      return "#/map";
    case "program":
      return `#/programs/${encodeURIComponent(r.id)}`;
    default:
      return "#/farm";
  }
}

export function go(r: Route) {
  location.hash = href(r);
}

export function useRoute(): Route {
  const [route, setRoute] = useState(() => parseRoute(location.hash));
  useEffect(() => {
    const on = () => {
      setRoute(parseRoute(location.hash));
      window.scrollTo(0, 0);
    };
    window.addEventListener("hashchange", on);
    return () => window.removeEventListener("hashchange", on);
  }, []);
  return route;
}
